import { action, computed, makeObservable, observable } from 'mobx';

import ApiService from '../services/ApiService';
import { currentDayOfMonth } from '../util/DateUtil';
import { formatAsNumberWithTwoDecimalPlaces } from '../util/FormatUtil';

import { IInvoicesStore } from './InvoicesStore';
import { Invoice } from './models/Invoice';
import { Loadable } from './models/Loadable';
import { Prepayment } from './models/Prepayment';
import { UnsettledReceivables } from './models/UnsettledReceivables';
import { ISsoStore } from './SsoStore';

export default class UnsettledReceivablesStore {
  @observable
  public unsettledReceivables: Loadable<UnsettledReceivables> = new Loadable<UnsettledReceivables>();
  public defaultPrepaymentAmount = 30.0;

  constructor(
    private ssoStore: ISsoStore,
    private apiService: ApiService,
    private invoicesStore: IInvoicesStore
  ) {
    makeObservable(this);
  }

  @computed
  public get allInvoicesPaid(): boolean {
    return this.unsettledReceivables.payload.unpaidInvoices.length === 0;
  }

  @computed
  public get hasPrepayments(): boolean {
    return this.unsettledReceivables.payload.prepayments.length > 0;
  }

  @computed
  public get prepayments(): Prepayment[] {
    return this.unsettledReceivables.payload.prepayments;
  }

  public get isInvoiceGenerationPeriod(): boolean {
    return 1 <= currentDayOfMonth() && currentDayOfMonth() < 5;
  }

  @computed
  public get totalAmountToPay(): number {
    return this.unsettledReceivables.payload.totalAmountToPay;
  }

  @computed
  public get totalAmountToPayFromUnpaidInvoices(): number {
    return this.unsettledReceivables.payload.unpaidInvoices
      .map((unpaidInvoices) => unpaidInvoices.unsettledAmount)
      .reduce((previous, current) => previous + current, 0);
  }

  @computed
  public get totalPrepayment(): number {
    return this.unsettledReceivables.payload.prepayments.map((prepayment) => prepayment.amount).reduce((previous, current) => previous + current, 0);
  }

  @computed
  public get unreservedTotalPrepayment(): number {
    return formatAsNumberWithTwoDecimalPlaces(
      this.unsettledReceivables.payload.prepayments
        .filter((prepayment) => !prepayment.reserved)
        .map((unreservedPrepayment) => unreservedPrepayment.amount)
        .reduce((previous, current) => previous + current, 0)
    );
  }

  @computed
  public get hasDebt(): boolean {
    return this.unsettledReceivables.payload.debtAmount > 0;
  }

  @action
  public async tryLoadUnsettledReceivables(): Promise<void> {
    if (!this.unsettledReceivables.isInitial) {
      await this.unsettledReceivables.promise;
      return;
    }

    const unsettledReceivablesPromise = this.apiService.getUnsettledReceivables(this.ssoStore.customer.customerId);

    const sortUnsettledReceivables = (unsettledReceivables: UnsettledReceivables) => {
      unsettledReceivables.unpaidInvoices = unsettledReceivables.unpaidInvoices.slice().sort(this.invoicesStore.sortIssueDateAscending);
      return unsettledReceivables;
    };

    this.unsettledReceivables = Loadable.toLoadable<UnsettledReceivables, UnsettledReceivables>(unsettledReceivablesPromise, sortUnsettledReceivables);

    await this.unsettledReceivables.promise;
  }

  public unpaidInvoice(invoiceNumber: string): Invoice {
    if (this.unsettledReceivables.isFinished) {
      const invoices = this.unsettledReceivables.payload.unpaidInvoices.filter((invoice) => invoice.invoiceNumber === invoiceNumber);
      return invoices.length !== 0 ? invoices[0] : null;
    }
    return null;
  }
}
